import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, useAuth } from "../../context/AuthContext";
import { ROLE_LABELS } from "./AdminLayout";
import { fmtDate } from "./Applications";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 transition-colors duration-300";

export default function AdminSettings() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("reviewer");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setUsers(await apiFetch("/admin/users"));
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (user && user.role !== "owner") {
    return <Navigate to="/admin/applications" replace />;
  }

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch("/admin/users/invite", {
        method: "POST",
        body: JSON.stringify({ name, email, role }),
      });
      toast.success(`Invitation sent to ${email}`);
      setName("");
      setEmail("");
      setRole("reviewer");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const act = async (path, successMsg, body) => {
    try {
      await apiFetch(path, {
        method: body ? "PATCH" : body === null ? "DELETE" : "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      toast.success(successMsg);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="admin-settings">
      <p className="text-xs uppercase tracking-[0.3em] text-secondary mb-2">
        Settings
      </p>
      <h1 className="font-display font-bold tracking-tighter text-3xl md:text-4xl mb-8">
        ADMIN USERS
      </h1>

      <form
        onSubmit={invite}
        data-testid="admin-invite-form"
        className="border border-white/10 rounded-xl p-5 md:p-6 bg-white/[0.02] mb-8"
      >
        <p className="text-[11px] uppercase tracking-[0.25em] text-secondary mb-4 flex items-center gap-2">
          <UserPlus size={14} /> Invite admin
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input
            data-testid="admin-invite-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className={inputCls}
          />
          <input
            data-testid="admin-invite-email"
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={inputCls}
          />
          <select
            data-testid="admin-invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={`${inputCls} uppercase text-xs tracking-[0.1em] [&>option]:bg-[#0a0a0a]`}
          >
            <option value="reviewer">Reviewer</option>
            <option value="owner">Owner</option>
          </select>
          <button
            type="submit"
            data-testid="admin-invite-submit"
            disabled={saving}
            className="bg-primary text-white rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] hover:brightness-110 transition-[filter] disabled:opacity-60"
          >
            {saving ? "Sending..." : "Invite admin"}
          </button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          The invitee receives a secure email link to create their own password.
        </p>
      </form>

      {!users ? (
        <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] animate-pulse py-10 text-center">
          Loading team
        </p>
      ) : (
        <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="admin-users-table">
          <div className="hidden md:grid grid-cols-[1.2fr_1.6fr_0.8fr_0.8fr_0.8fr_auto] gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <span>Name</span>
            <span>Email</span>
            <span>Role</span>
            <span>Status</span>
            <span>Date added</span>
            <span className="text-right">Actions</span>
          </div>
          {users.map((u) => (
            <div
              key={u.id}
              data-testid={`admin-user-row-${u.id}`}
              className="grid grid-cols-1 md:grid-cols-[1.2fr_1.6fr_0.8fr_0.8fr_0.8fr_auto] gap-2 md:gap-4 items-center px-5 py-4 border-b border-white/5 last:border-0"
            >
              <p className="font-semibold text-sm">{u.name}</p>
              <p className="text-sm text-muted-foreground truncate">{u.email}</p>
              <select
                data-testid={`admin-user-role-${u.id}`}
                value={u.role}
                disabled={u.id === user.id}
                onChange={(e) =>
                  act(`/admin/users/${u.id}/role`, "Role updated", { role: e.target.value })
                }
                className="w-fit bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-[11px] uppercase tracking-[0.1em] focus:outline-none disabled:opacity-50 [&>option]:bg-[#0a0a0a]"
              >
                <option value="owner">Owner</option>
                <option value="reviewer">Reviewer</option>
              </select>
              <p>
                <span
                  data-testid={`admin-user-status-${u.id}`}
                  className={`text-[10px] font-semibold uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${
                    u.status === "active"
                      ? "text-green-400 border-green-400/40"
                      : u.status === "invited"
                      ? "text-secondary border-secondary/40"
                      : "text-muted-foreground border-white/15"
                  }`}
                >
                  {u.status}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</p>
              <div className="flex items-center gap-2 md:justify-end">
                {u.id !== user.id && (
                  <>
                    {u.status === "inactive" ? (
                      <button
                        data-testid={`admin-user-reactivate-${u.id}`}
                        onClick={() => act(`/admin/users/${u.id}/reactivate`, "Account reactivated")}
                        className="text-[11px] uppercase tracking-[0.1em] text-secondary hover:text-foreground transition-colors"
                      >
                        Reactivate
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-user-deactivate-${u.id}`}
                        onClick={() => act(`/admin/users/${u.id}/deactivate`, "Account deactivated")}
                        className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Deactivate
                      </button>
                    )}
                    <button
                      data-testid={`admin-user-remove-${u.id}`}
                      onClick={() => {
                        if (window.confirm(`Remove access for ${u.name}?`)) {
                          act(`/admin/users/${u.id}`, "Access removed", null);
                        }
                      }}
                      className="text-muted-foreground hover:text-primary transition-colors"
                      aria-label="Remove access"
                    >
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
